import {
  Tooltip,
  Typography,
  Checkbox,
  CheckboxProps,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  TreeItem,
  TreeItemProps,
  TreeItemLabel,
  TreeItemSlots,
  TreeItemSlotProps,
} from "@mui/x-tree-view";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
  useMemo,
  memo,
} from "react";
import { SelectionRules } from "../hooks/useHierarchicalSelection";
import styles from "./TreeView.module.css";

export interface ExtendedTreeViewItem extends TreeViewBaseItem {
  tooltip?: string;
}

interface TreeViewProps {
  items: ExtendedTreeViewItem[];
  selectedItems?: string[];
  expandedItems?: string[];
  onSelectionChange?: (selectedItems: string[]) => void;
  isItemDisabled?: (item: TreeViewBaseItem) => boolean;
  id?: string;
  limitPopoverHeight?: boolean;
  selectionRules?: SelectionRules;
  showItemCounts?: boolean;
  checkboxTestId?: string;
}

// Custom checkbox component that includes IDs for Copilot
const CopilotCheckbox = React.forwardRef<
  HTMLButtonElement,
  CheckboxProps & { itemId?: string; checkboxTestId?: string }
>(function CopilotCheckbox(
  { itemId, checkboxTestId = "scoring-topic-checkbox-btn", ...props },
  ref
) {
  return (
    <Checkbox
      {...props}
      ref={ref}
      id={itemId ? `copilot-checkbox-${itemId}` : undefined}
      data-testid={checkboxTestId}
      style={{ padding: 0 }}
    />
  );
});

// Simple checkbox that prevents indeterminate state when needed
const NoIndeterminateCheckbox = React.forwardRef<
  HTMLButtonElement,
  CheckboxProps
>(function NoIndeterminateCheckbox(props, ref) {
  // Force indeterminate to false, ignore the prop
  return (
    <Checkbox
      {...props}
      ref={ref}
      indeterminate={false}
      style={{ padding: 0 }}
    />
  );
});

// Custom label component that shows item count
interface TreeItemLabelWithCountProps {
  children: React.ReactNode;
  itemId: string;
  countCache: Map<string, number>;
  // Allow additional props from MUI TreeItemLabel
  className?: string;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
}

const TreeItemLabelWithCount = memo<TreeItemLabelWithCountProps>(
  function TreeItemLabelWithCount({ children, itemId, countCache, ...props }) {
    const count = countCache.get(itemId);

    return (
      <TreeItemLabel {...props}>
        <Stack direction="row" alignItems="center" width="100%">
          <span>{children}</span>
          {count && count > 0 && (
            <Typography variant="h5" sx={{ ml: "auto", mr: 1 }}>
              ({count})
            </Typography>
          )}
        </Stack>
      </TreeItemLabel>
    );
  }
);

// Memoized TreeItem component with tooltip support
interface TreeItemWithTooltipProps extends TreeItemProps {
  tooltip?: string;
  selectionRules?: SelectionRules;
  showItemCounts?: boolean;
  countCache?: Map<string, number>;
  checkboxTestId?: string;
}

const TreeItemWithTooltip = memo<TreeItemWithTooltipProps>(
  function TreeItemWithTooltip({
    tooltip,
    selectionRules,
    showItemCounts,
    countCache,
    checkboxTestId,
    ...props
  }) {
    const slots: TreeItemSlots = {};

    // Add custom checkbox with ID for Copilot
    slots.checkbox = (checkboxProps: CheckboxProps) => (
      <CopilotCheckbox
        {...checkboxProps}
        itemId={props.itemId}
        checkboxTestId={checkboxTestId}
      />
    );

    // Override with no-indeterminate checkbox if needed
    if (selectionRules?.showIndeterminateCheckbox === false) {
      slots.checkbox = NoIndeterminateCheckbox;
    }

    // Add custom label if showing counts
    if (showItemCounts && countCache) {
      slots.label = TreeItemLabelWithCount;
    }

    const slotProps: TreeItemSlotProps & {
      label?: { itemId: string; countCache: Map<string, number> };
    } = {};

    // Add label props if showing counts
    if (showItemCounts && countCache) {
      slotProps.label = {
        itemId: props.itemId,
        countCache,
      };
    }

    const treeItemElement = (
      <TreeItem
        {...props}
        slots={Object.keys(slots).length > 0 ? slots : undefined}
        slotProps={Object.keys(slotProps).length > 0 ? slotProps : undefined}
      />
    );

    // If no tooltip, return the plain TreeItem
    if (!tooltip) {
      return treeItemElement;
    }

    // Wrap with Tooltip and let MUI handle the hover state internally
    return (
      <Tooltip
        title={<Typography variant="body2">{tooltip}</Typography>}
        placement="left"
        slotProps={{
          popper: { sx: { zIndex: 99999 } },
          tooltip: {
            sx: {
              bgcolor: "white",
              boxShadow: 1,
              "& .MuiTooltip-arrow": {
                color: "white",
              },
              width: "auto",
            },
          },
        }}
      >
        {treeItemElement}
      </Tooltip>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
      prevProps.itemId === nextProps.itemId &&
      prevProps.tooltip === nextProps.tooltip &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.label === nextProps.label &&
      // Check if selection-related props are the same
      prevProps.selectionRules === nextProps.selectionRules &&
      prevProps.showItemCounts === nextProps.showItemCounts &&
      prevProps.countCache === nextProps.countCache
    );
  }
);

/**
 * TreeView component that provides a hierarchical tree view with
 * advanced selection capabilities (cascading selection, parent-child relationships)
 */
export const TreeView: React.FC<TreeViewProps> = ({
  items,
  selectedItems = [],
  onSelectionChange,
  isItemDisabled,
  expandedItems,
  id = "tree-view",
  limitPopoverHeight,
  selectionRules,
  showItemCounts = false,
  checkboxTestId,
}) => {
  const handleSelectionChange = useCallback(
    (_event: SyntheticEvent<Element, Event> | null, itemIds: string[]) => {
      onSelectionChange?.(itemIds);
    },
    [onSelectionChange]
  );

  // Internal state for expanded items to handle manual expansions
  const [internalExpandedItems, setInternalExpandedItems] = useState<
    string[] | undefined
  >(expandedItems);

  // Update internal expanded items when props change
  useEffect(() => setInternalExpandedItems(expandedItems), [expandedItems]);

  // Handle expanded items changes from user interaction
  const handleExpandedItemsChange = useCallback(
    (_event: SyntheticEvent<Element, Event> | null, nodeIds: string[]) => {
      setInternalExpandedItems(nodeIds);
    },
    []
  );

  // Memoize selectionPropagation to avoid recreating object on every render
  const selectionPropagation = useMemo(
    () => ({
      descendants: selectionRules?.cascadeChildren ?? true,
      parents: selectionRules?.autoSelectParents ?? true,
    }),
    [selectionRules]
  );

  // Build tooltip cache efficiently without recursive search
  const tooltipCache = useMemo(() => {
    const cache = new Map<string, string>();

    // Build cache in a single pass using a stack for tree traversal
    const stack = [...items];
    while (stack.length > 0) {
      const item = stack.pop()!;
      if (item.tooltip) {
        cache.set(item.id, item.tooltip);
      }
      if (item.children) {
        stack.push(...item.children);
      }
    }

    return cache;
  }, [items]);

  // Build count cache for showing item counts
  const countCache = useMemo(() => {
    if (!showItemCounts) return new Map<string, number>();

    const cache = new Map<string, number>();

    // Build cache in a single pass using a stack for tree traversal
    const stack = [...items];
    while (stack.length > 0) {
      const item = stack.pop()!;
      const childCount = item.children?.length || 0;
      if (childCount > 0) {
        cache.set(item.id, childCount);
      }
      if (item.children) {
        stack.push(...item.children);
      }
    }

    return cache;
  }, [items, showItemCounts]);

  // Create a completely memoized render item component
  const MemoizedRenderItem = useMemo(() => {
    return memo<TreeItemProps>(
      (props) => {
        const tooltip = tooltipCache.get(props.itemId);
        return (
          <TreeItemWithTooltip
            {...props}
            tooltip={tooltip}
            selectionRules={selectionRules}
            showItemCounts={showItemCounts}
            countCache={countCache}
            checkboxTestId={checkboxTestId}
          />
        );
      },
      (prev, next) => {
        // Only re-render if the item ID or selection state changes
        return prev.itemId === next.itemId;
      }
    );
  }, [
    tooltipCache,
    selectionRules,
    showItemCounts,
    countCache,
    checkboxTestId,
  ]);

  return (
    <Box maxHeight={limitPopoverHeight ? 300 : undefined} id={id}>
      <RichTreeView
        id={`${id}-rich-tree`}
        multiSelect
        checkboxSelection
        selectionPropagation={selectionPropagation}
        className={styles.treeView}
        items={items}
        selectedItems={selectedItems}
        onSelectedItemsChange={handleSelectionChange}
        isItemDisabled={isItemDisabled}
        expandedItems={internalExpandedItems}
        onExpandedItemsChange={
          internalExpandedItems && handleExpandedItemsChange
        }
        disabledItemsFocusable={true}
        slots={{
          item: MemoizedRenderItem,
        }}
      />
    </Box>
  );
};
